import { ProjectEventType } from "@dramapie/shared";

/**
 * 项目实时事件流（M2）。
 *
 * 只做传输层该做的事：
 *   · 连接 / 断线指数退避重连（1s → 2s → 4s … 上限 30s）
 *   · 心跳：收到 ping 回 pong
 *   · 按 rev 丢弃过期帧（同一 rev 的多条事件是合法的，所以只丢 `< lastRev`）
 *   · snapshot 覆盖 rev 基线
 *
 * 业务语义（把事件合并进 StoryboardView / AgentView）不在这里做，
 * 由 `storyboard-commands.ts` 和 Agent 面板各自处理。
 */

export type StreamStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

/** 面板上的连接状态文案（顶栏 / Agent 头共用）。 */
export const STREAM_STATUS_LABEL: Record<StreamStatus, string> = {
  idle: "离线",
  connecting: "连接中",
  open: "实时",
  reconnecting: "重连中",
  closed: "离线",
};

/** 线上事件比 shared 的 ProjectEvent 略宽：`ping` 可能只有 `{ type }`。 */
export interface StreamMessage {
  type: ProjectEventType;
  rev: number;
  projectId: string;
  payload: Record<string, unknown>;
}

export type StreamListener = (message: StreamMessage) => void;

/** 只依赖浏览器 WebSocket 用到的这一小部分，测试里可替换成假实现。 */
export interface WebSocketLike {
  readyState: number;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type WebSocketCtor = new (url: string) => WebSocketLike;

const WS_OPEN = 1;
const BASE_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;

export interface ProjectStreamOptions {
  projectId: string;
  /** 默认按当前页面 origin 推导，测试可显式传入。 */
  url?: string;
  WebSocketImpl?: WebSocketCtor;
  onMessage?: StreamListener;
  onStatus?: (status: StreamStatus) => void;
  onError?: (error: unknown) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** 宽松解析：非法 JSON / 未知 type 直接丢弃，不打断连接。 */
export function parseStreamMessage(raw: unknown): StreamMessage | null {
  let value = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  if (!isRecord(value) || typeof value.type !== "string") return null;

  const type = ProjectEventType.safeParse(value.type);
  if (!type.success) return null;

  return {
    type: type.data,
    rev: typeof value.rev === "number" && Number.isFinite(value.rev) ? value.rev : 0,
    projectId: typeof value.projectId === "string" ? value.projectId : "",
    payload: isRecord(value.payload) ? value.payload : {},
  };
}

export function defaultWebSocketUrl(projectId: string): string {
  const location = globalThis.location;
  const protocol = location?.protocol === "https:" ? "wss:" : "ws:";
  const host = location?.host ?? "localhost";
  return `${protocol}//${host}/api/projects/${encodeURIComponent(projectId)}/ws`;
}

export class ProjectStream {
  private readonly projectId: string;
  private readonly url: string;
  private readonly options: ProjectStreamOptions;

  private socket: WebSocketLike | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private manualClose = false;
  private lastRev = 0;
  private status: StreamStatus = "idle";

  private readonly listeners = new Set<StreamListener>();
  private readonly statusListeners = new Set<(status: StreamStatus) => void>();

  constructor(options: ProjectStreamOptions) {
    this.options = options;
    this.projectId = options.projectId;
    this.url = options.url ?? defaultWebSocketUrl(options.projectId);
  }

  connect(): void {
    this.manualClose = false;
    this.attempt = 0;
    this.openSocket();
  }

  close(): void {
    this.manualClose = true;
    this.clearReconnectTimer();
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.setStatus("closed");
  }

  /** 命令响应 / 查询结果里的 rev 是新的基线，避免把同批事件当过期丢掉。 */
  noteRev(rev: number): void {
    if (Number.isFinite(rev) && rev > this.lastRev) this.lastRev = rev;
  }

  getLastRev(): number {
    return this.lastRev;
  }

  getStatus(): StreamStatus {
    return this.status;
  }

  subscribe(listener: StreamListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeStatus(listener: (status: StreamStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  private openSocket(): void {
    const Impl =
      this.options.WebSocketImpl ??
      (globalThis.WebSocket as unknown as WebSocketCtor | undefined);
    if (!Impl) {
      this.setStatus("closed");
      return;
    }

    this.setStatus(this.attempt > 0 ? "reconnecting" : "connecting");

    let socket: WebSocketLike;
    try {
      socket = new Impl(this.url);
    } catch (error) {
      this.options.onError?.(error);
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus("open");
    };
    socket.onmessage = (event) => {
      this.handleRaw(event?.data);
    };
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      if (this.manualClose) {
        this.setStatus("closed");
        return;
      }
      this.scheduleReconnect();
    };
    socket.onerror = (error) => {
      this.options.onError?.(error);
    };
  }

  private scheduleReconnect(): void {
    if (this.manualClose || this.reconnectTimer !== null) return;
    const delay = Math.min(MAX_RECONNECT_MS, BASE_RECONNECT_MS * 2 ** this.attempt);
    this.attempt += 1;
    this.setStatus("reconnecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.manualClose) return;
      this.openSocket();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private handleRaw(raw: unknown): void {
    const message = parseStreamMessage(raw);
    if (!message) return;

    // 心跳先于 rev 闸门：即使 rev 缺失/过期也要回 pong。
    if (message.type === "ping") {
      this.sendRaw({ type: "pong" });
      return;
    }

    if (message.type === "snapshot") {
      // snapshot 是新的真源，无论 rev 大小都覆盖基线。
      if (message.rev > this.lastRev) this.lastRev = message.rev;
      this.emit(message);
      return;
    }

    if (message.rev < this.lastRev) return;
    if (message.rev > this.lastRev) this.lastRev = message.rev;
    this.emit(message);
  }

  private sendRaw(value: unknown): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WS_OPEN) return;
    try {
      socket.send(JSON.stringify(value));
    } catch (error) {
      this.options.onError?.(error);
    }
  }

  private emit(message: StreamMessage): void {
    this.options.onMessage?.(message);
    for (const listener of [...this.listeners]) listener(message);
  }

  private setStatus(next: StreamStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.options.onStatus?.(next);
    for (const listener of [...this.statusListeners]) listener(next);
  }
}

/**
 * 一个稳定的订阅中心：WorkspacePage 建一条 WebSocket，把消息扇出给
 * 故事板和 Agent 面板，避免组件各自重连。
 */
export class StreamHub {
  private readonly listeners = new Set<StreamListener>();

  subscribe(listener: StreamListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit(message: StreamMessage): void {
    for (const listener of [...this.listeners]) listener(message);
  }
}
